import type { PerUnitProduct } from '@vy/services';
import { filterFixedProducts, isMeteredCreditProductOnly } from '@vy/services';
import type { Currency, Plan } from '@vy/types';
import { type FixedProduct } from '@vy/types';
import { isEmptyArray } from '@vy/utils';
import { FC } from 'react';
import { VyPaper } from '../../../../../components/new-design/data-display/VyPaper/VyPaper';
import { VyTable } from '../../../../../components/new-design/table/vy-table/VyTable';
import { useColumnState } from '../../../../../hooks/useColumnState';
import { ContractExpandableSection } from '../../ContractExpandableSection/ContractExpandableSection';
import { CONTRACT_PAGE_FIXED_FEES_MANDATORY_COLUMNS_KEYS } from '../../ContractFixedFees/contract-fixed-fees-const/contract-fixed-fees-mandatory-columns-keys';
import { getContractFixedFeesColumns } from '../../ContractFixedFees/contract-fixed-fees-utils/get-contract-fixed-fees-columns-keys';
import {
  buildFixedFeesRows,
  buildMeteredCreditRows,
} from '../../ContractFixedFees/contract-fixed-fees-utils/get-contract-fixed-fees-rows';

type ParentAgreementProductsSectionProps = {
  plan: Plan;
  currency: Currency;
};

export const ParentAgreementProductsSection: FC<ParentAgreementProductsSectionProps> = ({
  plan,
  currency,
}) => {
  const fixedProducts = filterFixedProducts<FixedProduct>(plan.products);
  const meteredCredits = plan.products.filter<PerUnitProduct>(isMeteredCreditProductOnly);
  const columns = getContractFixedFeesColumns(currency);
  const rows = buildFixedFeesRows(fixedProducts, plan);
  const meteredCreditsRows = buildMeteredCreditRows(meteredCredits);
  const discountAndCreditsRows = [...rows, ...meteredCreditsRows];

  const initial = useColumnState({
    columns,
    rows: discountAndCreditsRows,
    mandatoryKeys: CONTRACT_PAGE_FIXED_FEES_MANDATORY_COLUMNS_KEYS,
  });

  if (isEmptyArray(discountAndCreditsRows)) {
    return null;
  }

  return (
    <VyPaper>
      <ContractExpandableSection
        sx={{ padding: '0px 6px' }}
        title="Products"
        count={discountAndCreditsRows.length}
      >
        <VyTable
          tableName="parent-agreement-products"
          itemsCount={discountAndCreditsRows.length}
          items={discountAndCreditsRows}
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          sx={{
            maxHeight: '375px',
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 580,
            },
          }}
          useColumnVisibility={false}
          initialState={{
            columns: {
              columnVisibilityModel: {
                ...initial,
              },
            },
          }}
          showPagination={false}
          fadeOverlay
        />
      </ContractExpandableSection>
    </VyPaper>
  );
};
